import React, { useEffect, useState } from "react";
import ProductsService from "../../../api/services/products.service";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../../../components/common/loading/Loading";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import Select from "../../../components/common/select/Select";
import NestedSelect from "../../../components/common/nested-select/NestedSelect";
import Button from "../../../components/common/button/Button";
import styles from "./EditProducts.module.scss";
import {
  BrandsTypes,
  CategoriesTypes,
  CreateProductType,
  CurrencyTypes,
  ErrorsType,
  ProductsTypes,
} from "../../../api/types/products.types";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const EditProducts = () => {
  const { t } = useTranslation("translation");
  const [productDetail, setProductDetail] = useState<Partial<ProductsTypes>>(
    {},
  );
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<Partial<CreateProductType>>({});
  const [errors, setErrors] = useState<Partial<ErrorsType>>({});
  const [currency, setCurrency] = useState<CurrencyTypes[]>([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: categories, loaderRef } = useInfiniteScroll<CategoriesTypes>({
    fetchFn: async (page) => {
      const res = await ProductsService.getCategories(page, 20);
      return res.data;
    },
    pageSize: 20,
  });
  const { data: brands, loaderRef: brandsRef } = useInfiniteScroll<BrandsTypes>(
    {
      fetchFn: async (page) => {
        const res = await ProductsService.getBrands(page, 20);
        return res.data.data;
      },
      pageSize: 20,
    },
  );

  useEffect(() => {
    const getCurrency = async () => {
      const res = await ProductsService.getCurrency();
      setCurrency(res.data);
    };
    getCurrency();
  }, []);

  useEffect(() => {
    setLoading(true);
    const getDetails = async () => {
      try {
        const res = await ProductsService.getSingleProduct(Number(id));
        setProductDetail(res.data);
      } catch (error) {
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };
    getDetails();
  }, [id]);

  useEffect(() => {
    if (productDetail && productDetail.id) {
      setFormValues({
        brand_id: productDetail.brand_id || null,
        category_id: productDetail.category_id || null,
        currency_id: productDetail.currency_id || null,
      });
    }
  }, [productDetail]);

  const validateForm = (): boolean => {
    const newErrors: Partial<ErrorsType> = {};
    // if (!formValues.brand_id) newErrors.brand_id = t("required.brand") || "";
    if (!formValues.category_id)
      newErrors.category_id = t("required.category") || "";
    if (!formValues.currency_id)
      newErrors.currency_id = t("required.currency") || "";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof CreateProductType, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const formData = new FormData();
      if (
        formValues.brand_id &&
        Number(productDetail.brand_id) !== Number(formValues.brand_id)
      ) {
        formData.append("brand_id", String(formValues.brand_id));
      }
      if (
        formValues.currency_id &&
        Number(productDetail.currency_id) !== Number(formValues.currency_id)
      ) {
        formData.append("currency_id", String(formValues.currency_id));
      }
      if (
        formValues.category_id &&
        Number(productDetail.category_id) !== Number(formValues.category_id)
      ) {
        formData.append("category_id", String(formValues.category_id));
      }

      await ProductsService.updateProduct(Number(id), formData);
      toast.success(t("products_details.data_updated"));
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("error_message");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <TitleUpdater title={t("edit_product")} />
      <div className={styles.grid}>
        <div className={styles.left}>
          <div className={styles.form_category}>
            <p className={styles.title}>{t("products_details.specs")}</p>
            <div className={styles.form_content_column}>
              <NestedSelect
                label={t("columns.category") || ""}
                categories={categories}
                value={formValues.category_id || null}
                onChange={(value) => handleChange("category_id", value)}
                error={errors.category_id}
                className={styles.input}
                loaderRef={loaderRef}
                language={i18n.language}
              />
              <Select
                label={t("columns.currency") || ""}
                value={formValues.currency_id?.toString() || ""}
                onChange={(val) => {
                  if (val) {
                    handleChange("currency_id", val.id);
                  }
                }}
                options={currency.map((b) => ({
                  id: String(b.id),
                  name: b.name,
                }))}
                className={styles.input}
                error={errors.currency_id}
              />
              <Select
                label={t("columns.brand") || ""}
                value={formValues.brand_id?.toString() || ""}
                onChange={(val) => {
                  if (val) {
                    handleChange("brand_id", val.id);
                  }
                }}
                options={brands.map((b) => ({
                  id: String(b.id),
                  name: b.name,
                }))}
                className={styles.input}
                loaderRef={brandsRef}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.other_btns}>
        <Button type="submit" disabled={loading}>
          {loading ? t("buttons.saving") : t("buttons.update")}
        </Button>
        <Button
          type="button"
          className={styles.add_variant}
          onClick={() =>
            navigate(`/dashboard/products/edit-variants/${id}`, {
              state: { category_id: productDetail.category_id },
            })
          }
        >
          {t("products_details.go_to_variants")}
        </Button>
      </div>
    </form>
  );
};

export default EditProducts;
